import { Plus } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { View } from "tamagui";
import { TKey } from "../../../../shared/translations/translationKeys";
import { Box } from "../../../ui/Box";
import { Group } from "../../../ui/Group";
import type { IconProp } from "../../../ui/icons";
import { LoadingCircular } from "../../../ui/LoadingCircular";
import { ModuleDataType } from "../../core/BaseModuleDataModel";
import type { LocationModuleModelData } from "../LocationModel";
import type LocationModuleModel from "../LocationModuleModel";
import type { MapHandler } from "./Map";
import { PlacesAutoCompleteInput } from "./PlacesAutoCompleteInput";

const SpinnerIcon: IconProp = () => {
  return <LoadingCircular />;
};

export const LocationList: React.FC<{
  map: MapHandler;
  module: LocationModuleModel;
}> = observer(({ module, map }) => {
  const locationItems = module.locationItems;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    map.fitToMarkers();
  }, [map]);

  const handleAddLocation = useCallback(
    async (location: google.maps.places.PlacePrediction) => {
      const user = module.plan.userInPlan;

      if (!map || !location || !user) {
        return;
      }

      setLoading(true);

      const place = location.toPlace(); // Get first predicted place.
      await place.fetchFields({
        fields: ["displayName", "formattedAddress", "location"],
      });

      const placeLocation = place.location;
      if (placeLocation) {
        const mapData = {
          lat: placeLocation.lat(),
          lng: placeLocation.lng(),
        };

        await module.addNewData<LocationModuleModelData>(
          ModuleDataType.Location,
          {
            label: place.displayName ?? "",
            userId: user.uid,
            mapData,
          },
          TKey.ActivityLocationAdded,
          { label: place.displayName ?? "" },
        );
      }

      setLoading(false);
    },
    [module, map],
  );

  const firstLocationMapData = locationItems[0]?.data.mapData;

  return (
    <>
      {module.isDone ? undefined : (
        <View marginBottom={"$space.medium"}>
          <PlacesAutoCompleteInput
            disabled={loading}
            isLoading={loading}
            placeholder={loading ? "Adding location..." : "Add location..."}
            borderBottomWidth={1}
            locationBias={firstLocationMapData ?? undefined}
            blurOnSubmit={false}
            icon={loading ? SpinnerIcon : Plus}
            submitOnEnter
            type="padded"
            onSelect={handleAddLocation}
          />
        </View>
      )}
      {locationItems.length ? (
        <Box>
          <Group borderTop={false}>
            {locationItems.map((dataItem) => dataItem.render())}
          </Group>
        </Box>
      ) : undefined}
    </>
  );
});
